import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { color_groups } from '../../../../js/app/react/colors';
import ReactHighcharts from '../../../CommonComponents/Highcharts';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { isAdviserArea as isAdviserAreaSelector } from '../../../CommonSelectors/CommonAreaSelector';
import { ExternalSystemId } from '../../../ManageExcessCashModal/ManageExcessCashModal.meta';
import { CHART_OPTIONS } from '../RiskToleranceConstants';
import {
  ChartArea,
  ChartContainer,
  ChartSection,
  Detail,
  LabelText,
  LeftWrapper,
  Models,
  PortfolioItem,
  Portfolios,
  RiskBand,
  RiskinessImage,
  Wrapper,
} from '../RiskToleranceStyles';
import { GroupOptionsDTO } from '../RiskTolerance.types';

interface PortfolioPickerProps {
  groups: GroupOptionsDTO[];
  modelId: GroupOptionsDTO['modelPortfolios'][number]['id'] | null;
  groupId?: GroupOptionsDTO['groupId'] | null;
  onGroupChange?(groupId: GroupOptionsDTO['groupId']): void;
  onModelChange(modelId: GroupOptionsDTO['modelPortfolios'][number]['id'] | null): void;
  isHiddenLeversRTQ: boolean;
  showRiskToleranceWorkflow: boolean;
  showPortfolioProjectedReturns: boolean;
}

export const PortfolioPicker = ({
  groups,
  modelId,
  groupId,
  onGroupChange,
  onModelChange,
  isHiddenLeversRTQ,
  showRiskToleranceWorkflow,
  showPortfolioProjectedReturns,
}: PortfolioPickerProps) => {
  const isAdviserArea = useSelector(isAdviserAreaSelector);

  const [currentGroupId, setCurrentGroupId] = useState(groupId);

  const groupOptions = groups.map(group => ({
    ...group,
    label: group.groupName,
    value: group.groupId,
  }));

  const currentGroup = groupOptions.find(
    group =>
      group.groupId === currentGroupId || group.modelPortfolios.some(model => model.id === modelId),
  );

  const currentModel = currentGroup?.modelPortfolios.find(model => model.id === modelId);

  return (
    <Wrapper>
      <LeftWrapper>
        {isAdviserArea && (
          <>
            <LabelText>Select a portfolio group</LabelText>
            <Select
              aria-label="Select a portfolio group"
              name="selectedGroupId"
              isClearable={false}
              options={groupOptions}
              value={currentGroup}
              isPortal
              mb="24"
              onChange={opt => {
                if (opt !== null) {
                  onGroupChange?.(opt.groupId);

                  setCurrentGroupId(opt.groupId);

                  onModelChange(opt.defaultPortfolioId);
                }
              }}
            />
          </>
        )}

        {currentGroup && (
          <>
            <LabelText>
              Select a model portfolio based on your{' '}
              {isHiddenLeversRTQ ? 'risk profile' : 'risk tolerance'}
            </LabelText>
            <Portfolios>
              {currentGroup?.modelPortfolios.map(_model => (
                <PortfolioItem
                  key={_model.id}
                  isSelected={modelId === _model.id}
                  onClick={() => onModelChange(_model.id)}
                >
                  {currentGroup.externalSystem === ExternalSystemId.Riskalyze ? (
                    <RiskinessImage
                      alt="Riskalyze Risk"
                      src={`https://1dc018be4716da4aaa18-5d73971c6ce0cd6d739efc4134c4b48a.ssl.cf2.rackcdn.com/v2/standard/60/r${_model.riskScore}.png`}
                    />
                  ) : (
                    showRiskToleranceWorkflow && (
                      <RiskBand>{`${_model.riskRange.from}-${_model.riskRange.to}`}</RiskBand>
                    )
                  )}

                  <div>{_model.portfolioName}</div>
                </PortfolioItem>
              ))}
            </Portfolios>
          </>
        )}
      </LeftWrapper>

      <Models>
        <ChartArea isEmpty={!modelId}>
          {currentModel && (
            <>
              <ChartSection sameLine>
                <LabelText>Portfolio Name</LabelText>
                <Detail>{currentModel.portfolioName}</Detail>
              </ChartSection>
              <ChartSection>
                <LabelText>Asset Allocation</LabelText>
                <ChartContainer>
                  <ReactHighcharts
                    options={{
                      ...CHART_OPTIONS,
                      colors: currentModel.assetAllocation
                        .filter(({ y }) => y > 0)
                        .map(
                          ({ color, color_group: colorGroup }) =>
                            color || color_groups[colorGroup][0],
                        ),
                      series: [
                        {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                          // @ts-ignore
                          ...CHART_OPTIONS.series[0],
                          data: currentModel.assetAllocation
                            .filter(({ y }) => y > 0)
                            .map(({ name, y }) => ({
                              name,
                              y,
                            })),
                        },
                      ],
                    }}
                  />
                </ChartContainer>
              </ChartSection>
              {currentModel.portfolioDescription && (
                <ChartSection>
                  <LabelText>Description</LabelText>
                  <Detail>{currentModel.portfolioDescription}</Detail>
                </ChartSection>
              )}

              {showPortfolioProjectedReturns && (
                <ChartSection>
                  <LabelText>Projected Return</LabelText>
                  <Detail>{currentModel.expectedReturn}</Detail>
                </ChartSection>
              )}
            </>
          )}
        </ChartArea>
      </Models>
    </Wrapper>
  );
};
